import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DeviceService } from '@omni/services/device/device.service';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { UIService } from '@omni/services/ui/ui.service';
import { ModalController, PopoverController } from '@ionic/angular';
import { ProfilePopupComponent } from '../popover/profile-popup/profile-popup.component';

import { EventsService } from '@omni/services/events/events.service';
import { MyAssistantService } from '@omni/services/my-assistant/my-assistant.service';
import { ActivityService } from '@omni/services/activity/activity.service';
import { ActivityCreationService, ProcedureLogOption } from '@omni/services/activity/activity-creation.service';
import { MeetingActivityType } from '@omni/classes/activity/activity.class';
import { FeatureActionsService } from '@omni/services/feature-actions/feature-actions.service';
import { IndNotificationDataModel } from '@omni/models/indNotificationDataModel';
import { RepServices } from '@omni/data-services/rep/rep.services';
import { AuthenticationDataService } from '@omni/data-services/authentication/authentication.service';
import { LAST_REP_STATUS_KEY, REP_STATUS } from '@omni/models/rep-status-model';
import { some } from 'lodash-es';
import { DirectMessagingService } from '@omni/services/direct-messaging/direct-messaging.service';
import { EmailActivityChannelDetails } from '@omni/classes/consent/channel.class';
import {
  IndActionMenuOption, IndActionMenuResult, IndActionMenuSection,
} from '../shared/ind-action-menu/ind-action-menu';
import { CaseManagementService } from '@omni/services/case-management/case-management.service';
import { GlobalUtilityService } from '@omni/services/global-utility.service';
import { FOLLOW_UP_TYPE } from '@omni/classes/activity/follow-up-action.activity.class';
import { DiskService } from '@omni/services/disk/disk.service';
import { QuickActionMenuService } from '@omni/services/activity/quick-action-menu.service';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';

interface QuickCreateType {
  /** Sub-list for the overflow menu, when the type asks something before creating. */
  menuOptions?: () => IndActionMenuOption[];
  /** Runs a type picked from the overflow, given the option id the rep chose there. */
  runWithOptionId?: (optionId?: string) => Promise<void>;
  dictateWithOptionId?: (optionId?: string) => Promise<void>;
  labelKey: string;
  /** Customer Inquiry's label carries the BU's configured noun for a customer. */
  labelParams?: () => Record<string, string>;
  icon: string;
  isEnabled: () => boolean;
  /** Offered but inert: the translation key saying why, or null while it can run. */
  disabledReason?: () => string | null;
  run: (event: Event) => Promise<void>;
}
import { UserProfilePhotoService } from '@omni/services/user-profile-photo/user-profile-photo.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';

// Their details carry the glance card's own quick action strip.
const CUSTOMER_TOOL_PAGES = [PageName.ContactPageComponent, PageName.AccountPageComponent];
const PIN_CAP = 4;
const DEFAULT_PINS = ['MEETING', 'MESSAGE', 'NEW_ACTIVITY_PHONECALL'];

@Component({
  selector: 'go-app-title',
  templateUrl: './go-app-title.component.html',
  styleUrl: './go-app-title.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GoAppTitleComponent {
  public isProfilePopupOpen = false;

  public backgroundUploadInProgress: boolean = false;
  public syncInProgress: boolean = false;
  public localDbDataLoadInProgress = false;
  public syncMessage = this.translate.instant('UPLOAD_IN_PROGRESS');
  public hasBaselineEnabled: boolean = false;

  isAndroid: boolean = false;
  // A tap on a native device fires the hover tooltip, which then lingers over the header.
  readonly showTooltips = !(this.device.isAndroid() || this.device.isIOS());
  isHCPFacing = false;
  modelView;
  @Output() newActivityOpened = new EventEmitter<boolean>();
  public isInCustomerTool = false;
  private profilePopover: HTMLIonPopoverElement;
  hasAnyUnReadNotifications: boolean = false;
  /** Data URL of the signed-in user's photo, or null to show the initials. */
  userPhoto: string | null = null;


  selected;

  constructor(public authService: AuthenticationService,
    public readonly device: DeviceService,
    private readonly translate: TranslateService,
    private readonly navService: NavigationService,
    private readonly uiService: UIService,
    private readonly modalCtrl: ModalController,
    private readonly popoverCtrl: PopoverController,
    public readonly activityService: ActivityService,
    private readonly events: EventsService,
    public myAssistantService: MyAssistantService,
    private readonly _cd: ChangeDetectorRef,
    private readonly quickActionMenu: QuickActionMenuService,
    private readonly repService: RepServices,
    private readonly authenticationDataService: AuthenticationDataService,
    private readonly activityCreationService: ActivityCreationService,
    private readonly featureActionService: FeatureActionsService,
    private readonly directMessagingService: DirectMessagingService,
    private readonly caseManagementService: CaseManagementService,
    private readonly utilityService: GlobalUtilityService,
    private readonly disk: DiskService,
    private readonly userProfilePhoto: UserProfilePhotoService,
    private readonly notificationService: NotificationService,
  ) {

  }

  public createInFlight = false;
  public isMeetingEnabled = true;
  public isKitBookingEnabled = false;
  public isMessageEnabled = false;
  private hasMessageActivity = false;
  public isAllocationOrderEnabled = false;
  public isPhoneCallEnabled = false;
  public isFollowUpActionEnabled = false;
  private hasCaseIntake = false;
  public isAccountVisitEnabled = false;
  public isSalesOrderEnabled = false;
  public isStoreCheckEnabled = false;
  private hasMedicalInquiry = false;
  public isCustomerInsightEnabled = false;
  public isTimeOffEnabled = false;
  private messagingChannels: EmailActivityChannelDetails[] = [];

  /** Declaration order is the bar's priority order: what drops to the overflow first. */
  private readonly quickCreateTypes: QuickCreateType[] = [
    {
      labelKey: 'MEETING', icon: 'topbaricons/appointment.svg', isEnabled: () => this.isMeetingEnabled,
      run: event => this.createMeeting(event),
      menuOptions: () => this.activityCreationService.meetingFormatOptions(),
      runWithOptionId: id => this.activityCreationService.create('meeting', {
        meetingFormat: this.meetingFormats.find(format => format.indskr_activitytypeid === id),
      }).then(() => {}),
      dictateWithOptionId: id => this.activityCreationService.dictateMeeting(id).then(() => {}),
    },
    {
      labelKey: 'MESSAGE', icon: 'topbaricons/email.svg', isEnabled: () => this.isMessageEnabled,
      run: event => this.createMessage(event),
      menuOptions: () => this.messagingChannels.map(channel => ({
        id: channel.id,
        label: this.directMessagingService.translateNewActivityChannelTitle(channel.title),
      })),
      runWithOptionId: id => this.activityCreationService.create('message', {
        messageChannel: this.messagingChannels.find(channel => channel.id === id),
      }).then(() => {}),
    },
    { labelKey: 'NEW_ACTIVITY_PHONECALL', icon: 'topbaricons/phone-call-activity.svg', isEnabled: () => this.isPhoneCallEnabled, run: () => this.createPhoneCall() },
    {
      labelKey: 'ACCOUNT_VISIT', icon: 'topbaricons/account_visit.svg', isEnabled: () => this.isAccountVisitEnabled,
      disabledReason: () => this.activityCreationService.offlineRefusal('accountVisit'), run: () => this.createAccountVisit(),
    },
    { labelKey: 'ALLOWCATION_ORDER', icon: 'topbaricons/customer-allocations.svg', isEnabled: () => this.isAllocationOrderEnabled, run: () => this.createAllocationOrder() },
    {
      labelKey: 'FOLLOW_UP_ACTION', icon: 'topbaricons/follow-up_32x32.svg',
      isEnabled: () => this.isFollowUpActionEnabled, run: event => this.createFollowUpAction(event),
      menuOptions: () => this.activityCreationService.followUpCategories()
        .map(category => ({ id: category.value, label: category.label })),
      runWithOptionId: id => this._runFollowUp(id as FOLLOW_UP_TYPE),
    },
    {
      labelKey: 'CUSTOMER_INQUIRY_WITH_TEXT', icon: 'topbaricons/case-intake.svg',
      // Lazy: the BU noun is resolved through translate, which is not loaded at construction.
      labelParams: () => ({ globalCustomerText: this.utilityService.globalCustomerText }),
      isEnabled: () => this.hasCaseIntake && this.caseManagementService.caseTypeList?.length > 0,
      run: event => this.createCustomerInquiry(event),
      menuOptions: () => (this.caseManagementService.caseTypeList ?? []).map(caseType => ({
        id: caseType.id, label: caseType.name, note: caseType.description,
      })),
      runWithOptionId: id => this.activityCreationService.create('customerInquiry', {
        caseType: this.caseManagementService.caseTypeList.find(caseType => caseType.id === id),
      }).then(() => {}),
    },
    {
      labelKey: 'NEW_ACTIVITY_PROCEDURE_LOG', icon: 'topbaricons/order_open.svg',
      isEnabled: () => this.activityCreationService.procedureLogCategories().length > 0, run: event => this.createProcedureLog(event),
      menuOptions: () => this.activityCreationService.procedureLogCategories().map(category => ({ id: category.value, label: category.label })),
      runWithOptionId: id => this._runProcedureLog(id as ProcedureLogOption),
    },
    { labelKey: 'NEW_ACTIVITY_SALES_ORDER', icon: 'topbaricons/sales-orders.svg', isEnabled: () => this.isSalesOrderEnabled, run: () => this.createSalesOrder() },
    {
      labelKey: 'STORE_CHECK', icon: 'topbaricons/storecheck.svg', isEnabled: () => this.isStoreCheckEnabled,
      disabledReason: () => this.activityCreationService.offlineRefusal('storeCheck'), run: () => this.createStoreCheck(),
    },
    {
      // ponytail: no medical-inquiry glyph exists; this collides with Customer Inquiry's.
      // No expert-category submenu: MI has no functional association with them, so the FA alone
      // decides, same as the New Activity row and the contact strip.
      labelKey: 'MEDICAL_INQUIRY', icon: 'topbaricons/case-intake.svg',
      isEnabled: () => this.hasMedicalInquiry,
      run: event => this.createMedicalInquiry(event),
    },
    // ponytail: no Insights+ glyph exists; trending-up is a stand-in, not the real icon.
    {
      labelKey: 'XPERIENCES_INSIGHTS_PLUS', icon: 'trending-up.svg', isEnabled: () => this.isCustomerInsightEnabled,
      disabledReason: () => this.activityCreationService.offlineRefusal('customerInsight'), run: () => this.createCustomerInsight(),
    },
    {
      labelKey: 'KIT_BOOKING', icon: 'topbaricons/kit-booking.svg', isEnabled: () => this.isKitBookingEnabled,
      disabledReason: () => this.activityCreationService.offlineRefusal('kitBooking'), run: () => this.createKitBooking(),
    },
    { labelKey: 'TIME_OFF', icon: 'topbaricons/time-off.svg', isEnabled: () => this.isTimeOffEnabled, run: () => this.createTimeOff() },
  ];
  public visibleTypes: QuickCreateType[] = [];
  public overflowTypes: QuickCreateType[] = [];
  private pinCap = PIN_CAP;
  /**
   * The strip the rep chose, by labelKey, in pin order. `null` means untouched, which falls
   * back to declaration order; an empty array is a rep who unpinned everything, which is a
   * legitimate state and must not be confused with the first. Persisted to the local database,
   * which is already per user and per instance, so the key needs no scoping.
   */
  private stripIds: string[] | null = null;

  /**
   * Four on the bar, the rest behind the overflow. The bar has no wrap or overflow handling,
   * so a glyph past the cap is clipped off the end rather than wrapped — it has to go in the
   * menu to stay reachable. One on a phone: the full wordmark leaves room for a single
   * compliant touch target plus the overflow, nothing more.
   */
  private splitTypesForWidth(): void {
    const enabled = this.quickCreateTypes.filter(type => type.isEnabled());
    this.pinCap = window.innerWidth < 430 ? 1 : PIN_CAP;

    // Untouched: the default pins, then the next enabled types in declaration order.
    if (this.stripIds === null) {
      this.visibleTypes = this._enabledInDefaultOrder().slice(0, this.pinCap);
      this.overflowTypes = enabled.filter(type => !this.visibleTypes.includes(type));
      return;
    }
    // Chosen: the strip is exactly what the rep pinned, and it does not backfill — a bar
    // shorter than the cap is a normal state, and backfilling would silently undo an unpin.
    // A pin that no longer resolves (feature action off) is skipped, not dropped, so it
    // returns if the type does.
    this.visibleTypes = this.stripIds
      .map(id => enabled.find(type => type.labelKey === id))
      .filter(Boolean)
      .slice(0, this.pinCap) as QuickCreateType[];
    this.overflowTypes = enabled.filter(type => !this.visibleTypes.includes(type));
  }

  private _pin(labelKey: string): void {
    if (this.visibleTypes.length >= this.pinCap) return;
    // The new pin goes ahead of any id that does not resolve yet, so when that type comes back
    // slice() drops it past the cap rather than the pin just made. It is kept, not pruned:
    // channels and case types resolve only after sync, so pruning would delete those pins.
    const enabledIds = new Set(this.quickCreateTypes.filter(type => type.isEnabled()).map(type => type.labelKey));
    const strip = this._currentStripIds().filter(id => id !== labelKey);
    this.stripIds = [
      ...strip.filter(id => enabledIds.has(id)),
      labelKey,
      ...strip.filter(id => !enabledIds.has(id)),
    ];
    this.splitTypesForWidth();
    this._cd.detectChanges();
    this._saveStrip();
  }

  private _unpin(labelKey: string): void {
    this.stripIds = this._currentStripIds().filter(id => id !== labelKey);
    this.splitTypesForWidth();
    this._cd.detectChanges();
    this._saveStrip();
  }

  /**
   * What the rep has chosen so far. Until the first pin the strip is implicit, and those types
   * have to become explicit or the first unpin would drop all of them at once. Only what the
   * current width shows becomes explicit: defaults kept past the cap backfilled every unpin on a phone.
   */
  private _currentStripIds(): string[] {
    return this.stripIds ?? this._enabledInDefaultOrder().slice(0, this.pinCap).map(type => type.labelKey);
  }

  private _enabledInDefaultOrder(): QuickCreateType[] {
    const enabled = this.quickCreateTypes.filter(type => type.isEnabled());
    const defaults = enabled.filter(type => DEFAULT_PINS.includes(type.labelKey))
      .sort((a, b) => DEFAULT_PINS.indexOf(a.labelKey) - DEFAULT_PINS.indexOf(b.labelKey));
    return [...defaults, ...enabled.filter(type => !defaults.includes(type))];
  }

  private async _loadStrip(): Promise<void> {
    try {
      const doc = await this.disk.retrieve(DB_KEY_PREFIXES.QUICK_CREATE_PINNED, true);
      if (!Array.isArray(doc?.raw)) return;
      this.stripIds = doc.raw;
      this.splitTypesForWidth();
      this._cd.detectChanges();
    } catch (error) {
      // The toolbar can be torn down while the read is in flight, which makes detectChanges throw.
      console.error('Quick create: could not read the pinned strip', error);
    }
  }

  /** Fire and forget: a pin that fails to persist has still been applied to the bar. */
  private _saveStrip(): void {
    this.disk.updateOrInsert(DB_KEY_PREFIXES.QUICK_CREATE_PINNED, doc => ({ ...doc, raw: this.stripIds }))
      .catch(error => console.error('Quick create: could not persist the pinned strip', error));
  }

  private _buildMenuSections(): IndActionMenuSection[] {
    const isFull = this.visibleTypes.length >= this.pinCap;
    const toItem = (t: QuickCreateType, pinned: boolean) => {
      const disabledReason = t.disabledReason?.();
      return {
        id: t.labelKey,
        label: this.translate.instant(t.labelKey, t.labelParams?.()),
        icon: `assets/imgs/${t.icon}`,
        disabled: !!disabledReason,
        disabledReason: disabledReason ? this.translate.instant(disabledReason) : undefined,
        options: t.menuOptions?.(),
        dictatable: !!t.dictateWithOptionId && this.activityCreationService.canDictateMeeting(),
        pinned,
        pinnable: true,
        // Greyed, not hidden — the counter above explains why it will not take.
        pinDisabled: !pinned && isFull,
      };
    };

    const sections: IndActionMenuSection[] = [];
    if (this.overflowTypes.length) {
      sections.push({
        title: this.translate.instant('MORE_ACTIVITY_TYPES'),
        warning: isFull ? this.translate.instant('UNPIN_TO_ADD_ANOTHER') : undefined,
        counter: isFull ? undefined : this.translate.instant('N_OF_M_PINNED', {
          count: this.visibleTypes.length, cap: this.pinCap,
        }),
        items: this.overflowTypes.map(type => toItem(type, false)),
      });
    }
    if (this.visibleTypes.length) {
      sections.push({
        title: this.translate.instant('PINNED_ACTIVITIES'),
        items: this.visibleTypes.map(type => toItem(type, true)),
      });
    }
    return sections;
  }

  /** The menu surface is shared with the glance cards; the zone-escape repaint is ours. */
  private async _presentActionMenu(event: Event, componentProps: Record<string, unknown>)
    : Promise<IndActionMenuResult | undefined> {
    const data = await this.quickActionMenu.present(event, componentProps);
    // onDidDismiss can resolve outside the Angular zone (OMNI-60723), so markForCheck would never run.
    this._cd.detectChanges();
    return data;
  }

  public async openOverflow(event: Event): Promise<void> {
    const data = await this._presentActionMenu(event, {
      sections: this._buildMenuSections(),
      // Pinning manages the bar rather than choosing from it, so the menu stays open and
      // redraws with the type moved between sections.
      onPinToggle: (result: IndActionMenuResult) => {
        if (result.action === 'pin') this._pin(result.itemId);
        else this._unpin(result.itemId);
        return this._buildMenuSections();
      },
    });
    if (!data || (data.action !== 'select' && data.action !== 'dictate')) return;

    // Rebuilt after any pinning, so the type is looked up against the current split.
    const type = [...this.overflowTypes, ...this.visibleTypes].find(candidate => candidate.labelKey === data.itemId);
    if (!type) return;
    if (data.action === 'dictate') {
      await type.dictateWithOptionId?.(data.optionId);
      return;
    }
    // A sub-list row carries the choice, so the type must not ask for it a second time. With no
    // options (Meeting, no formats) the row is the choice; run() would reopen the heading-only dropdown.
    if (type.runWithOptionId && (data.optionId || !type.menuOptions?.().length)) {
      await type.runWithOptionId(data.optionId);
      return;
    }
    await type.run(event);
  }

  // A disabled tile stays tappable so it can say why instead of silently doing nothing.
  public async runType(type: QuickCreateType, event: Event): Promise<void> {
    const disabledReason = type.disabledReason?.();
    if (disabledReason) {
      this.notificationService.notify(this.translate.instant(disabledReason), 'Quick Create', 'top', ToastStyle.INFO);
      return;
    }
    await type.run(event);
  }

  public async createTimeOff(): Promise<void> {
    await this.activityCreationService.create('timeOff');
  }

  public async createKitBooking(): Promise<void> {
    await this.activityCreationService.create('kitBooking');
  }

  public async createMedicalInquiry(event: Event): Promise<void> {
    if (await this._runFromSubList(event, 'MEDICAL_INQUIRY')) return;
    await this.activityCreationService.create('medicalInquiry', {});
  }

  public async createCustomerInsight(): Promise<void> {
    await this.activityCreationService.create('customerInsight');
  }

  public async createStoreCheck(): Promise<void> {
    await this.activityCreationService.create('storeCheck');
  }

  public async createSalesOrder(): Promise<void> {
    await this.activityCreationService.create('salesOrder');
  }

  public async createAccountVisit(): Promise<void> {
    await this.activityCreationService.create('accountVisit');
  }

  public async createProcedureLog(event: Event): Promise<void> {
    if (await this._runFromSubList(event, 'NEW_ACTIVITY_PROCEDURE_LOG')) return;
    await this._runProcedureLog(this.activityCreationService.procedureLogCategories()[0]?.value);
  }

  private async _runProcedureLog(option: ProcedureLogOption | undefined): Promise<void> {
    await this.activityCreationService.createProcedureLogOf(option);
  }

  public async createCustomerInquiry(event: Event): Promise<void> {
    if (await this._runFromSubList(event, 'CUSTOMER_INQUIRY_WITH_TEXT')) return;
    await this.activityCreationService.create('customerInquiry', { caseType: this.caseManagementService.caseTypeList?.[0] });
  }

  public async createFollowUpAction(event?: Event): Promise<void> {
    if (event && await this._runFromSubList(event, 'FOLLOW_UP_ACTION')) return;
    await this._runFollowUp(this.activityCreationService.followUpCategories()[0]?.value);
  }

  private async _runFollowUp(category: FOLLOW_UP_TYPE): Promise<void> {
    await this.activityCreationService.create('followUpAction', { followUpCategory: category });
  }

  public async createPhoneCall(): Promise<void> {
    await this.activityCreationService.create('phoneCall');
  }

  public async createAllocationOrder(): Promise<void> {
    await this.activityCreationService.create('allocationOrder');
  }

  public async createMessage(event: Event): Promise<void> {
    if (await this._runFromSubList(event, 'MESSAGE')) return;
    await this.activityCreationService.create('message', { messageChannel: this.messagingChannels[0] });
  }

  private get meetingFormats(): MeetingActivityType[] {
    return this.activityCreationService.meetingFormats();
  }

  public async createMeeting(event: Event): Promise<void> {
    if (await this._runFromSubList(event, 'MEETING')) return;
    await this.activityCreationService.create('meeting', { meetingFormat: this.meetingFormats[0] });
  }

  /**
   * The dropdown a bar tile opens: the type's own option rows, which is what keeps a
   * configured note — only a sub-list renders one, a flat row has nowhere to put it.
   *
   * Returns false when there is no choice to offer, so the caller creates on its default.
   */
  private async _runFromSubList(event: Event, labelKey: string): Promise<boolean> {
    const type = this.quickCreateTypes.find(candidate => candidate.labelKey === labelKey);
    const options = type?.menuOptions?.() ?? [];
    const dictatable = !!type?.dictateWithOptionId && this.activityCreationService.canDictateMeeting();
    if (!type?.runWithOptionId || (options.length <= 1 && !dictatable)) return false;

    const picked = await this.quickActionMenu.pickOption(event, type.labelKey,
      this.translate.instant(type.labelKey, type.labelParams?.()), options, dictatable);
    // onDidDismiss escapes the zone (OMNI-60723), so markForCheck would never run.
    this._cd.detectChanges();
    if (!picked) return true;
    if (picked.dictate) await type.dictateWithOptionId?.(picked.optionId);
    else await type.runWithOptionId(picked.optionId);
    return true;
  }

  public getInitials(): string {
    if (!this.authService.user?.displayName) return '';
    const name = this.authService.user.displayName;
    const nameParts = name.split(' ');
    if (nameParts.length === 1) {
      return name.slice(0, 2).toUpperCase();
    }
    return nameParts
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  private _readFeatureActions(): void {
    const fa = (featureAction: FeatureActionsMap) => this.authService.hasFeatureAction(featureAction);
    this.hasBaselineEnabled = fa(FeatureActionsMap.BASELINE);
    // Mirrors isMeetingEnabled in new-activity: the only unconditional disable, and it
    // hides the row there rather than dimming it.
    this.isMeetingEnabled = !(this.featureActionService.isShortCallLauncherEnabledInMobileDevice
      && fa(FeatureActionsMap.SHOR_CALL_LAUNCHER_DISABLE_APPOINTMENT_IN_NEW_ACTIVITY));
    this.isKitBookingEnabled = fa(FeatureActionsMap.KIT_BOOKING);
    this.hasMessageActivity = fa(FeatureActionsMap.MESSAGE_ACTIVITY);
    this.isMessageEnabled = this.hasMessageActivity && this.messagingChannels.length > 0;
    this.isPhoneCallEnabled = fa(FeatureActionsMap.PHONECALL_ACTIVITY);
    // Shares the meeting's short-call suppression, which hides both rows in new-activity.
    this.isAccountVisitEnabled = fa(FeatureActionsMap.ACCOUNT_VISIT) && this.isMeetingEnabled;
    this.isSalesOrderEnabled = fa(FeatureActionsMap.ORDER_MANAGEMENT);
    this.isStoreCheckEnabled = fa(FeatureActionsMap.STORE_CHECK);
    this.hasMedicalInquiry = fa(FeatureActionsMap.MEDICAL_INQUIRY_TOOL);
    this.isCustomerInsightEnabled = fa(FeatureActionsMap.XPERIENCES);
    this.isTimeOffEnabled = fa(FeatureActionsMap.TIME_OFF_TOOL);
    this.isFollowUpActionEnabled = fa(FeatureActionsMap.FOLLOW_UP_ACTION_ACTIVITY);
    // The case type is what the inquiry is created against, so with none configured the icon
    // has nothing to create — the New Activity row disables itself on the same condition.
    // The list arrives with master data sync, so the count is read at split time, not here.
    this.hasCaseIntake = fa(FeatureActionsMap.CASE_INTAKE);
    this.isAllocationOrderEnabled = fa(FeatureActionsMap.ALLOCATION_TOOL) && fa(FeatureActionsMap.ALLOCATION_ORDER_ACTIVITY);
  }

  ngOnInit() {
    // Deliberately not awaited: the bar renders in declaration order and is corrected once the
    // read returns, rather than holding up the toolbar behind a disk hit.
    this._loadStrip();
    this._readFeatureActions();
    // A message is always sent on a channel, so with none configured the icon has nothing
    // to create — same rule the New Activity row uses to disable itself.
    this.directMessagingService.channelList.subscribe(channels => {
      // Alphabetical, as New Activity lists them, so every Message picker matches (OMNI-63297).
      this.messagingChannels = [...(channels || [])].sort((channelA, channelB) => channelA.title.localeCompare(channelB.title));
      this.isMessageEnabled = this.hasMessageActivity && this.messagingChannels.length > 0;
      this.splitTypesForWidth();
      this._cd.detectChanges();
    });
    this.events.observe('FA Refreshed').subscribe(() => {
      this._readFeatureActions();
      this.splitTypesForWidth();
      this._cd.detectChanges();
    });
    this.splitTypesForWidth();
    this.navService.currentPageStack$.subscribe(stack => {
      const isInCustomerTool = CUSTOMER_TOOL_PAGES.includes(stack[stack.length - 1]?.pageName);
      if (isInCustomerTool === this.isInCustomerTool) return;
      this.isInCustomerTool = isInCustomerTool;
      this._cd.detectChanges();
    });
    this.isAndroid = this.device.isAndroid();
    this.device.isBackgroundUploadInProgressObservable.subscribe(inProgress => {
      this.backgroundUploadInProgress = inProgress;
      this.syncMessage = this.translate.instant('OFFLINE_UPLOAD_IN_PROGRESS');
      this._cd.detectChanges();
    });

    this.device.syncInProgress$.subscribe(inProgress => {
      this.syncInProgress = inProgress;
      if (!this.backgroundUploadInProgress) {
        this.syncMessage = this.translate.instant('UPLOAD_IN_PROGRESS');
      }
      // Sync is what fills the lists the gates read, so the bar can gain a type here.
      if (!inProgress) this.splitTypesForWidth();
      this._cd.detectChanges();
      if (!inProgress) {
        this.refreshUserPhoto();
      }
    });

    this.device.isDataLoadFromLocalDB.subscribe((inProgress) => {
      this.localDbDataLoadInProgress = inProgress;
      this.syncMessage = this.translate.instant('INIT_DATA_IN_PROGRESS');
      this._cd.detectChanges();
    });

    this.device.isOfflineObservable.subscribe(() => {
      this._cd.markForCheck();
      this.refreshUserPhoto();
    });

    this.userProfilePhoto.photo$.subscribe((photo) => {
      this.userPhoto = photo;
      this._cd.detectChanges();
    });

    this.activityCreationService.inFlight$.subscribe(inFlight => {
      this.createInFlight = inFlight;
      this._cd.detectChanges();
    });

    this.myAssistantService.notificationsObs$.subscribe((data: IndNotificationDataModel[]) => {
      this.hasAnyUnReadNotifications = some(data, ['isRed', false]);
      this._cd.markForCheck();
    });
    this._initRepUserState();
  }

  /**
   * The tooltip directive shows and hides outside change detection, so on OnPush the view
   * does not repaint until something marks it dirty — the tooltip would appear only on the
   * next unrelated event, which reads as showing on mouse-out.
   */
  onTooltipVisibilityChange() {
    this._cd.markForCheck();
  }

  /** Shows the signed-in user's cached photo and, when online, brings it up to date from Microsoft Graph. */
  private refreshUserPhoto(): void {
    void this.userProfilePhoto.load(!this.device.isOffline);
  }

  get ngClass(){
    return {
      'offline-indicator-root': this.device.isOffline,
      'toolbar-android': this.isAndroid
    }
  }

  private async _initRepUserState() {
    if (this.repService && this.authenticationDataService) {
      if (this.authenticationDataService.isOfflineLoginFlow) {
        if (this.repService.wasLastUserStateOffline()) {
          this.device.isUserStateOffline = this.repService.isOfflineState = true;
          this.repService.setCurrentUserState(REP_STATUS.OFFLINE.userState);
        }
        this.device.isOffline = true;
      } else if (!this.device.isOffline) {
        try {
          await this.repService.getCurrentState(true);
          const isUserWithoutStatus = !this.repService.getCurrentUserState();
          this.repService.setCurrentUserState(REP_STATUS.ONLINE.userState);
          const newStateData = { userState: REP_STATUS.ONLINE.userState };
          await this.repService.setCurrentState(newStateData).then(res => {
            window.localStorage.setItem(LAST_REP_STATUS_KEY, REP_STATUS.ONLINE.userState);
          }).catch(() => { });
          if (isUserWithoutStatus) {
            await this.repService.getCurrentState();
          }
        } catch (error) {
          console.error('go-app-title: _initRepUserState: ', error);
        }
      }
    }
    this._cd.markForCheck();
  }


  @HostListener('window:resize')
  onResize() {
    if (this.profilePopover) {
      this.profilePopover.dismiss();
    }
    this.isProfilePopupOpen = false;
    this.splitTypesForWidth();
    this._cd.detectChanges();
  }

  async openProfile(event) {
    this.isProfilePopupOpen = true;
    let target = event.target || event.currentTarget;
    if (target?.tagName?.toLowerCase() !== 'ion-button') {
      target = target.closest('ion-button') || target;
    }
    const rect = target.getBoundingClientRect();
    const fakeEvent = {
      target: {
        getBoundingClientRect: () => rect
      }
    };
    this.profilePopover = await this.popoverCtrl.create({
      component: ProfilePopupComponent,
      cssClass: 'profile-popup',
      event: fakeEvent as any,
      translucent: true,
      backdropDismiss: true
    });
    this.profilePopover.onDidDismiss().then(() => {
      this.isProfilePopupOpen = false;
      this._cd.detectChanges();
    });
    await this.profilePopover.present();
  }

  async syncData() {
    this.activityService.deltaSyncData();
    this.navService.setRootWithTracking(PageName.GoAppActivitiesPageComponent, PageName.GoAppActivitiesPageComponent, { __source: 'dashboard' }, PageName.GoAppActivitiesPageComponent).then(() => {
      if (this.device.isMobileDevicePortrait) {
        this.uiService.toolsActivityActive = true;
        this.events.publish('footer:selectedViewChanged', 'dashboard');
        this.events.publish('setSelectedNavView', 'dashboard');
      }
    });
  }

  public async openNewActivity() {
    if (this.navService.getCurrentPageName() != PageName.GoAppActivitiesPageComponent) {
      this.newActivityOpened.emit(true);
      await this.navService.setRootWithTracking(PageName.GoAppActivitiesPageComponent, PageName.GoAppActivitiesPageComponent, { __source: 'dashboard' }, PageName.GoAppActivitiesPageComponent);
    }
    this.activityService.activitySource = null;
    this.events.publish('newActivityOpenedChanged', true);
      this.uiService.showRightPane = true;
      this.uiService.showRightPaneOnly = false;
      if (this.navService.getActiveChildNavViewPageName() != PageName.NothingSelectedView) {
        await this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView, PageName.GoAppHomeComponent);
      }
      await this.navService.pushChildNavPageWithPageTracking(PageName.NewActivityComponent, PageName.NewActivityComponent, PageName.GoAppActivitiesPageComponent,
        {
          from: PageName.GoAppHomeComponent,
          callbackEvent: this._handleNewActivityCallback.bind(this),
        });
  }

  async openNotifications() {
    if (this.selected == 'notifications') {
      return;
    }
    this.activityService.activityOpenedFromTodo = false;
    if (this.device.isMobileDevicePortrait) {
      this.uiService.showRightPane = false;
      this.activityService.selectedActivity = null;
    }
    this.selected = 'notifications';
    this.navService.setRootWithTracking(PageName.GoAppNotificationPageComponent, PageName.GoAppNotificationPageComponent, {
      callback: () => this.selected = null,
      __source: 'menu'
    }, PageName.GoAppHomeComponent);
  }

  public _handleNewActivityCallback(data: any, destroyed: boolean) {
    this.events.publish('newActivityOpenedChanged', false);
    if (this.modelView) {
      this.modelView.dismiss();
      this.modelView = undefined;
    } else if (!destroyed && !data) {
      this.uiService.showRightPane = false;
    }
  }
}
